package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.DragEvent
import top.wcpe.allincore.ui.runtime.event.KeyEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.ScrollEvent
import top.wcpe.allincore.ui.runtime.ir.eval.EventMap

/**
 * 事件 → Map 转换协作者：把运行期事件对象转成 DSL 可访问的 `Map<String, Any?>`。
 *
 * 纯叶子组件——无 Context、无递归、无对外框架依赖；门面 [IrInterpreter] 与
 * [EventBindingDispatcher] 经 `IrInterpreter.xxxEventToMap(...)` 转发到此。
 */
@Suppress("MagicNumber")
internal object EventMapBuilder {

    /**
     * 把 KeyEvent 转为 DSL 可访问的 Map（ExprEvaluator 的 MemberAccess 已支持 Map.get(member)）。
     *
     * 暴露字段：
     * - `key` - 字符串键名（`"E"` / `"Escape"` / `"F1"` 等；
     *   完整命名见 [top.wcpe.allincore.common.input.key.Keys]；未识别为 ""）
     * - `scanCode` - 原始扫描码
     * - `shift` / `ctrl` / `alt` - 修饰键便捷字段（Boolean）
     * - `action` - 0=release / 1=press / 2=repeat
     */
    fun keyEventToMap(event: KeyEvent): Map<String, Any?> {
        val mods = event.modifiers()
        val m: EventMap = EventMap.ofKey(event)
        m.put("key", event.key())
        m.put("scanCode", event.scanCode().toDouble())
        m.put("modifiers", mods.toDouble())
        m.put("action", event.action().toDouble())
        // GLFW 标准修饰位：SHIFT=0x1, CTRL=0x2, ALT=0x4, SUPER=0x8
        m.put("shift", (mods and 0x1) != 0)
        m.put("ctrl", (mods and 0x2) != 0)
        m.put("alt", (mods and 0x4) != 0)
        // 玩家 keymap 中绑定的 translation key（如 "key.swapOffhand"）；非 fabric / 未命中为 ""。
        // DSL 可写 if (event.vanillaKey == "key.swapOffhand") event.consume()。
        m.put("vanillaKey", event.vanillaKey())
        // 按键已按住的累计毫秒数：press=0；repeat / release 为按下到当前事件的时间差。
        // DSL 可写 if (event.action == 0 && event.holdMs >= 1000) skip()。
        m.put("holdMs", event.holdMs().toDouble())
        return m
    }

    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 2：把 MouseEvent 转为 DSL 可访问的 Map（与 keyEventToMap 对称）。
     *
     * 暴露字段：
     * - `x` / `y` - 鼠标坐标
     * - `button` - 字符串按键名（`"Left"` / `"Right"` / `"Middle"` /
     *   `"MouseBack"` 等；hotkey 触发时为空字符串）。
     *   与 RFC §5.2 一致——DSL 中可直接写 `event.button == "Right"`。
     * - `buttonCode` - 原始 GLFW 按键编号（DSL 极少用到，留作兜底）
     */
    fun mouseEventToMap(event: MouseEvent): Map<String, Any?> {
        val m: EventMap = EventMap.ofMouse(event)
        m.put("x", event.x())
        m.put("y", event.y())
        // 本地坐标（相对于命中元素 box 左上角）；DSL 用 event.localX / localY 直接做 hitArc/hitRect
        m.put("localX", event.localX())
        m.put("localY", event.localY())
        // RFC §5.2：DSL 中 button 字段直接放字符串名，避免 `event.button == "Right"` 永远 false
        m.put("button", event.buttonName())
        m.put("buttonCode", event.button().toDouble())
        return m
    }

    /**
     * RFC-INPUT-EVENT-UNIFICATION Stage 2：把 ChangeEvent 转为 DSL 可访问的 Map。
     *
     * Stage 2 仅暴露最常用的 `value` / `oldValue` 两个字段，对所有元素
     * （Slider/Switch/Checkbox/TextField/RadioGroup/Dropdown/TabView）通用——它们的
     * ChangeEvent 类型形参不同（Number/Boolean/String 等），但运行期都是
     * [top.wcpe.allincore.ui.runtime.event.ChangeEvent] record，字段访问统一。
     */
    fun changeEventToMap(event: ChangeEvent<*>): Map<String, Any?> {
        val m = LinkedHashMap<String, Any?>()
        // ChangeEvent 已是 Kotlin data class（访问器经 @get:JvmName 保留 Java 侧 newValue()/oldValue() 名）；
        // Kotlin 侧按属性访问。
        m["value"] = normalizeChangeFieldValue(event.newValue)
        m["oldValue"] = normalizeChangeFieldValue(event.oldValue)
        return m
    }

    /**
     * v4 C.2：把 [DragEvent] 转为 DSL 可访问的 Map。
     *
     * 暴露字段：
     * - `mouseX` / `mouseY` - 当前鼠标坐标（屏幕/根坐标系）
     * - `button` - 按键编号（0=左键，1=右键，2=中键），与 DragEvent 原始字段一致
     * - `deltaX` / `deltaY` - 相对上一帧的位移
     */
    fun dragEventToMap(event: DragEvent): Map<String, Any?> {
        val m = LinkedHashMap<String, Any?>()
        // DragEvent 已是 Kotlin data class（访问器经 @get:JvmName 保留 Java 侧名）；Kotlin 侧按属性访问。
        m["mouseX"] = event.mouseX
        m["mouseY"] = event.mouseY
        m["button"] = event.button.toDouble()
        m["deltaX"] = event.deltaX
        m["deltaY"] = event.deltaY
        return m
    }

    /**
     * v4 C.2：把 [ScrollEvent] 转为 DSL 可访问的 Map。
     *
     * 暴露字段：
     * - `mouseX` / `mouseY` - 当前鼠标坐标
     * - `scrollX` / `scrollY` - 滚动量（正负代表方向，单位「行/格」）
     */
    fun scrollEventToMap(event: ScrollEvent): Map<String, Any?> {
        val m = LinkedHashMap<String, Any?>()
        m["mouseX"] = event.mouseX()
        m["mouseY"] = event.mouseY()
        m["scrollX"] = event.scrollX()
        m["scrollY"] = event.scrollY()
        return m
    }

    /**
     * 把 ChangeEvent 的值规范化为 DSL 数值约定：JVM 数值统一为 Double，
     * 其它类型（Boolean / String / null / 列表索引等）原样透传，避免 DSL 比较时
     * 因 Integer/Long 与 Double 不等而误判。
     */
    private fun normalizeChangeFieldValue(v: Any?): Any? {
        if (v is Number && v !is Double) {
            return v.toDouble()
        }
        return v
    }
}
